// @ts-nocheck
import React, { useEffect, useRef } from 'react';

export default function SpaceBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    if (!ctx) return;

    let W: number, H: number;
    let stars: any[] = [];
    let shoots: any[] = [];
    let shockwaves: any[] = [];
    let galaxies: any[] = [];
    let frame = 0;
    let shootTimer = 0;
    let lmx = -999;
    let lmy = -999;
    let bhReady = false;
    let animationFrameId: number;

    const CHARGE_RATE = 1 / 100;
    const DECAY_RATE = 1 / 420;

    const bh = {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      r: 36,
      lr: 115,
      pulse: 0,
      charging: false,
      flashFrame: -999,
    };

    const nebCfg = [
      { cx: 0.12, cy: 0.20, blobs: [[0, 0, 0.26, [165, 18, 88, 0.26]], [0.07, 0.05, 0.15, [215, 50, 68, 0.18]], [-0.05, 0.09, 0.11, [255, 90, 140, 0.12]]] },
      { cx: 0.80, cy: 0.26, blobs: [[0, 0, 0.24, [15, 52, 190, 0.24]], [-0.08, 0.06, 0.14, [30, 100, 230, 0.16]], [0.06, -0.07, 0.10, [65, 150, 255, 0.10]]] },
      { cx: 0.52, cy: 0.87, blobs: [[0, 0, 0.22, [30, 118, 95, 0.20]], [0.09, -0.05, 0.13, [15, 158, 128, 0.14]], [-0.06, 0.04, 0.09, [60, 200, 160, 0.09]]] },
      { cx: 0.90, cy: 0.66, blobs: [[0, 0, 0.20, [82, 8, 165, 0.22]], [-0.06, 0.07, 0.12, [122, 20, 208, 0.16]], [0.04, -0.05, 0.08, [160, 60, 240, 0.10]]] },
      { cx: 0.28, cy: 0.74, blobs: [[0, 0, 0.18, [8, 78, 162, 0.18]], [0.06, -0.04, 0.11, [20, 120, 205, 0.12]]] },
      { cx: 0.60, cy: 0.10, blobs: [[0, 0, 0.18, [190, 48, 10, 0.18]], [-0.07, 0.06, 0.11, [230, 85, 22, 0.12]], [0.05, -0.02, 0.08, [255, 140, 50, 0.08]]] },
      { cx: 0.42, cy: 0.50, blobs: [[0, 0, 0.14, [100, 20, 180, 0.10]], [0.06, 0.05, 0.09, [60, 10, 140, 0.07]]] },
    ];

    // 7 distinct real-world galaxy morphological types
    const galDefs = [
      { cx: 0.74, cy: 0.14, rf: 0.105, flatY: 0.28, angle: -0.38, arms: 2, r0: 148, g0: 188, b0: 255, type: 'spiral' },    // Grand design spiral (like M51), BIG
      { cx: 0.13, cy: 0.52, rf: 0.064, flatY: 0.32, angle: 1.22, arms: 2, r0: 255, g0: 212, b0: 125, type: 'barred' },    // Barred spiral (like NGC 1300), golden
      { cx: 0.87, cy: 0.45, rf: 0.058, flatY: 0.44, angle: 0.65, arms: 5, r0: 172, g0: 148, b0: 255, type: 'flocculent' },// Flocculent spiral (like M33), violet
      { cx: 0.37, cy: 0.07, rf: 0.055, flatY: 0.11, angle: -1.05, arms: 2, r0: 255, g0: 190, b0: 145, type: 'edge_on' },   // Edge-on spiral (like NGC 891), orange
      { cx: 0.54, cy: 0.64, rf: 0.050, flatY: 0.56, angle: 0.90, arms: 2, r0: 255, g0: 228, b0: 168, type: 'elliptical' },// Elliptical (like M87), cream
      { cx: 0.06, cy: 0.83, rf: 0.054, flatY: 0.50, angle: 1.82, arms: 2, r0: 80, g0: 200, b0: 210, type: 'irregular' }, // Irregular (like LMC), teal
      { cx: 0.83, cy: 0.83, rf: 0.048, flatY: 0.40, angle: -0.52, arms: 2, r0: 200, g0: 176, b0: 255, type: 'ring' },      // Ring galaxy (like Cartwheel), lavender
    ];

    function makeGalParticles(r: number, arms: number, type: string) {
      const ps = [];
      const n = Math.floor(r * 3.2);
      
      if (type === 'elliptical') {
        // Smooth de Vaucouleurs r^(1/4) profile — dense core, gentle falloff
        for (let i = 0; i < n; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 0.38) * r * 0.88;
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.52 + 0.12,
            alpha: (1 - d / (r * 0.88)) * 0.82 * (Math.random() * 0.30 + 0.48),
            warm: true,
          });
        }
      } else if (type === 'barred') {
        // Central bar + two arms launching from bar ends
        const barLen = r * 0.44;
        for (let i = 0; i < Math.floor(n * 0.28); i++) {
          const bx = (Math.random() * 2 - 1) * barLen;
          const by = (Math.random() - 0.5) * r * 0.11;
          ps.push({
            x: bx,
            y: by,
            r: Math.random() * 0.50 + 0.14,
            alpha: (0.36 + 0.56 * (1 - Math.abs(bx) / barLen)) * (Math.random() * 0.28 + 0.52),
            warm: Math.abs(bx) < barLen * 0.5,
          });
        }
        for (let i = 0; i < Math.floor(n * 0.72); i++) {
          const side = i % 2 === 0 ? 1 : -1;
          const baseAng = side > 0 ? 0 : Math.PI;
          const t = Math.random() * Math.PI * 1.75;
          const d = (t / (Math.PI * 1.75)) * (r - barLen * 0.72) + barLen * 0.72;
          const ang = baseAng + t * 1.55 + (Math.random() - 0.5) * 0.40;
          const sd = (Math.random() - 0.5) * r * 0.07;
          const sa = ang + Math.PI / 2;
          ps.push({
            x: Math.cos(ang) * d + Math.cos(sa) * sd,
            y: Math.sin(ang) * d + Math.sin(sa) * sd,
            r: Math.random() * 0.55 + 0.12,
            alpha: Math.max(0.06, 1 - (d / r) * 0.64) * (Math.random() * 0.40 + 0.22),
            warm: d < r * 0.42,
          });
        }
        for (let i = 0; i < 20; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 2) * r * 0.14;
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.44 + 0.13,
            alpha: 0.62 + Math.random() * 0.30,
            warm: true,
          });
        }
      } else if (type === 'flocculent') {
        // Many patchy, loosely wound arms — no grand coherent spiral
        const na = 5;
        for (let i = 0; i < n; i++) {
          const arm = i % na;
          const off = (arm / na) * Math.PI * 2;
          const t = Math.random() * Math.PI * 1.55;
          const d = (t / (Math.PI * 1.55)) * r * (0.62 + Math.random() * 0.38);
          const ang = off + t * 1.32 + (Math.random() - 0.5) * 1.05;
          const sd = (Math.random() - 0.5) * r * 0.17;
          const sa = ang + Math.PI / 2;
          ps.push({
            x: Math.cos(ang) * d + Math.cos(sa) * sd,
            y: Math.sin(ang) * d + Math.sin(sa) * sd,
            r: Math.random() * 0.54 + 0.12,
            alpha: Math.max(0.05, 0.80 - (d / r) * 0.66) * (Math.random() * 0.48 + 0.15),
            warm: d < r * 0.30,
          });
        }
        for (let i = 0; i < 20; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 2) * r * 0.16;
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.44 + 0.13,
            alpha: 0.65 + Math.random() * 0.28,
            warm: true,
          });
        }
      } else if (type === 'edge_on') {
        // Thin disk seen from the side — flatY squishes to a sliver
        for (let i = 0; i < n; i++) {
          const x = (Math.random() * 2 - 1) * r;
          const y = (Math.random() - 0.5) * r * 0.55;
          const d = Math.abs(x);
          ps.push({
            x,
            y,
            r: Math.random() * 0.55 + 0.12,
            alpha: Math.max(0.04, (1 - (d / r) * 0.70) * 0.90) * (Math.random() * 0.35 + 0.26),
            warm: d < r * 0.20,
          });
        }
        // Extra central bulge density
        for (let i = 0; i < 20; i++) {
          const x = (Math.random() - 0.5) * r * 0.28;
          const y = (Math.random() - 0.5) * r * 0.28;
          ps.push({
            x,
            y,
            r: Math.random() * 0.44 + 0.13,
            alpha: 0.60 + Math.random() * 0.32,
            warm: true,
          });
        }
      } else if (type === 'irregular') {
        // Clumpy, asymmetric — like the Large Magellanic Cloud
        const nc = 5 + Math.floor(Math.random() * 4);
        const clumps = [];
        for (let c = 0; c < nc; c++) {
          clumps.push({
            x: (Math.random() * 2 - 1) * r * 0.66,
            y: (Math.random() * 2 - 1) * r * 0.50,
            size: r * (0.14 + Math.random() * 0.26),
            bright: Math.random(),
          });
        }
        for (let i = 0; i < n; i++) {
          const cl = clumps[i % clumps.length];
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 0.60) * cl.size;
          ps.push({
            x: cl.x + Math.cos(a) * d,
            y: cl.y + Math.sin(a) * d,
            r: Math.random() * 0.58 + 0.13,
            alpha: (0.26 + cl.bright * 0.44) * (Math.random() * 0.44 + 0.26),
            warm: cl.bright > 0.56,
          });
        }
      } else if (type === 'ring') {
        // Annular ring with compact nucleus — like the Cartwheel galaxy
        const iR = r * 0.46;
        const oR = r * 0.90;
        const mid = (iR + oR) / 2;
        const hw = (oR - iR) / 2;
        for (let i = 0; i < Math.floor(n * 0.76); i++) {
          const a = Math.random() * Math.PI * 2;
          const d = iR + Math.random() * (oR - iR);
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.56 + 0.14,
            alpha: (0.32 + 0.58 * (1 - Math.abs(d - mid) / hw)) * (Math.random() * 0.32 + 0.28),
            warm: false,
          });
        }
        for (let i = 0; i < Math.floor(n * 0.24); i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 1.5) * iR * 0.50;
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.46 + 0.13,
            alpha: 0.50 + Math.random() * 0.44,
            warm: true,
          });
        }
        // Spokes connecting ring to nucleus (faint)
        for (let i = 0; i < 6; i++) {
          const sa = (i / 6) * Math.PI * 2;
          for (let j = 0; j < 4; j++) {
            const d = iR * 0.55 + (j * (iR * 0.45)) / 4;
            ps.push({
              x: Math.cos(sa) * d,
              y: Math.sin(sa) * d,
              r: 0.22,
              alpha: 0.15 + j * 0.04,
              warm: false,
            });
          }
        }
      } else {
        // Grand design spiral — clean symmetric arms
        for (let i = 0; i < n; i++) {
          const arm = i % arms;
          const off = (arm / arms) * Math.PI * 2;
          const t = Math.random() * Math.PI * 2.1;
          const d = (t / (Math.PI * 2.1)) * r;
          const ang = off + t * 1.65 + (Math.random() - 0.5) * 0.48;
          const sd = (Math.random() - 0.5) * r * 0.09;
          const sa = ang + Math.PI / 2;
          ps.push({
            x: Math.cos(ang) * d + Math.cos(sa) * sd,
            y: Math.sin(ang) * d + Math.sin(sa) * sd,
            r: Math.random() * 0.58 + 0.13,
            alpha: Math.max(0.07, 1 - (d / r) * 0.62) * (Math.random() * 0.46 + 0.20),
            warm: d < r * 0.32,
          });
        }
        for (let i = 0; i < 22; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.pow(Math.random(), 2) * r * 0.18;
          ps.push({
            x: Math.cos(a) * d,
            y: Math.sin(a) * d,
            r: Math.random() * 0.44 + 0.13,
            alpha: 0.70 + Math.random() * 0.28,
            warm: true,
          });
        }
      }
      return ps;
    }

    function initGalaxies() {
      galaxies = galDefs.map((def) => {
        const r = def.rf * Math.min(W, H);
        return { ...def, r, particles: makeGalParticles(r, def.arms, def.type) };
      });
    }

    // Adjusted weights: small bg stars +30%, giant stars -20%, renormalized
    const starVariants = [
      [0, 0, 100, 0.12, 0.80, 0.28, false],
      [0, 0, 100, 0.12, 0.80, 0.28, false],
      [208, 82, 96, 0.22, 1.10, 0.33, false],
      [46, 92, 90, 0.28, 1.20, 0.36, false],
      [24, 88, 83, 0.30, 1.25, 0.33, false],
      [350, 72, 80, 0.28, 1.10, 0.31, false],
      [280, 60, 88, 0.20, 1.00, 0.30, false],
      [168, 65, 88, 0.22, 1.00, 0.30, false],
      [0, 0, 100, 1.7, 3.0, 0.80, true],
      [208, 78, 96, 1.8, 3.2, 0.82, true],
      [46, 88, 92, 1.6, 2.8, 0.80, true],
      [24, 82, 85, 1.5, 2.5, 0.75, true],
      [350, 70, 82, 1.5, 2.4, 0.72, true],
    ];
    const starW = [0.296, 0.167, 0.147, 0.101, 0.073, 0.046, 0.037, 0.037, 0.037, 0.022, 0.015, 0.015, 0.007];

    let initialized = false;
    // Lock to initial viewport size to prevent iOS address bar resize jitter
    const initialW = window.innerWidth;
    const initialH = window.innerHeight;
    function resize() {
      W = canvas.width = initialW;
      H = canvas.height = initialH;
      if (!bhReady) {
        const a = Math.random() * Math.PI * 2;
        bh.x = W * (0.25 + Math.random() * 0.5);
        bh.y = H * (0.25 + Math.random() * 0.5);
        bh.vx = Math.cos(a) * 0.3;
        bh.vy = Math.sin(a) * 0.2;
        bhReady = true;
      }
      // Only create stars/galaxies once — don't re-randomize on resize/scroll
      if (!initialized) {
        makeStars();
        initGalaxies();
        initialized = true;
      }
    }

    function makeStars() {
      stars = [];
      const cw = [];
      let s = 0;
      for (const w of starW) {
        s += w;
        cw.push(s);
      }
      for (let i = 0; i < 585; i++) {
        const rv = Math.random();
        let vi = 0;
        for (let j = 0; j < cw.length; j++) {
          if (rv < cw[j]) {
            vi = j;
            break;
          }
        }
        const v = starVariants[vi];
        const r = v[3] + Math.random() * (v[4] - v[3]);
        stars.push({
          x: Math.random() * W,
          y: Math.random() * H,
          r,
          base: v[5] + Math.random() * (1 - v[5]) * 0.4,
          ts: Math.random() * 0.022 + 0.003,
          to: Math.random() * Math.PI * 2,
          hue: v[0],
          sat: v[1],
          lit: v[2],
          giant: v[6],
        });
      }
    }

    function spawnShoot(x: number | null, y: number | null, burst: boolean) {
      const n = burst ? 3 + Math.floor(Math.random() * 2) : 1;
      for (let c = 0; c < n; c++) {
        setTimeout(() => {
          const a = Math.PI * 0.3 + (Math.random() - 0.5) * (burst ? 1.8 : 0.5);
          const spd = burst ? 4 + Math.random() * 5 : 7 + Math.random() * 6;
          shoots.push({
            x: (x ?? Math.random() * W * 0.8) + (burst ? (Math.random() - 0.5) * 14 : 0),
            y: (y ?? Math.random() * H * 0.4) + (burst ? (Math.random() - 0.5) * 14 : 0),
            vx: Math.cos(a) * spd,
            vy: Math.sin(a) * spd,
            trail: [],
            tl: 20 + Math.floor(Math.random() * 18),
            life: 1,
            decay: 0.011 + Math.random() * 0.007,
            fromMouse: x != null,
          });
        }, burst ? c * 100 : 0);
      }
    }

    function lensed(x: number, y: number) {
      const dx = x - bh.x;
      const dy = y - bh.y;
      const d = Math.hypot(dx, dy);
      if (d < bh.r * 0.85) return null;
      if (d > bh.lr) return { x, y, a: 1, s: 1 };
      const t = Math.pow((bh.lr - d) / (bh.lr - bh.r), 1.6);
      const push = t * bh.r * 2.8;
      const ang = Math.atan2(dy, dx);
      return {
        x: bh.x + Math.cos(ang) * (d + push),
        y: bh.y + Math.sin(ang) * (d + push),
        a: 1 - t * 0.55,
        s: 1 + t * 0.35,
      };
    }

    function drawNebulae() {
      for (const neb of nebCfg) {
        for (const [dx, dy, rr, rgba] of neb.blobs) {
          const gx = (neb.cx + dx) * W;
          const gy = (neb.cy + dy) * H;
          const gr = rr * Math.min(W, H);
          const g = ctx.createRadialGradient(gx, gy, 0, gx, gy, gr);
          g.addColorStop(0, `rgba(${rgba[0]},${rgba[1]},${rgba[2]},${rgba[3]})`);
          g.addColorStop(0.42, `rgba(${rgba[0]},${rgba[1]},${rgba[2]},${(rgba[3] * 0.42).toFixed(3)})`);
          g.addColorStop(1, `rgba(${rgba[0]},${rgba[1]},${rgba[2]},0)`);
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(gx, gy, gr, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    function drawGalaxies() {
      for (const gal of galaxies) {
        const gx = gal.cx * W;
        const gy = gal.cy * H;
        ctx.save();
        ctx.translate(gx, gy);
        ctx.rotate(gal.angle);
        
        // Tight inner disk glow only
        const mg = ctx.createRadialGradient(0, 0, 0, 0, 0, gal.r * 0.82);
        mg.addColorStop(0, `rgba(${gal.r0},${gal.g0},${gal.b0},0.16)`);
        mg.addColorStop(0.55, `rgba(${gal.r0},${gal.g0},${gal.b0},0.05)`);
        mg.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = mg;
        ctx.beginPath();
        ctx.ellipse(0, 0, gal.r * 0.82, gal.r * 0.82 * gal.flatY, 0, 0, Math.PI * 2);
        ctx.fill();
        
        // Particles
        const wr = Math.min(255, gal.r0 + 28);
        const wg = Math.min(255, gal.g0 + 28);
        const wb = Math.min(255, gal.b0 + 52);
        for (const p of gal.particles) {
          ctx.globalAlpha = p.alpha;
          ctx.fillStyle = p.warm
            ? `rgb(${gal.r0},${gal.g0},${Math.floor(gal.b0 * 0.72 + 46)})`
            : `rgb(${wr},${wg},${wb})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y * gal.flatY, p.r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        
        // Core bulge — skip for irregular, smaller for ring, special handling for edge-on
        if (gal.type !== 'irregular') {
          const coreR = gal.type === 'ring' ? gal.r * 0.14 : gal.type === 'edge_on' ? gal.r * 0.18 : gal.r * 0.26;
          const flatClamp = gal.type === 'edge_on' ? Math.max(0.55, gal.flatY * 4.5) : Math.max(0.40, gal.flatY * 1.55);
          const cg = ctx.createRadialGradient(0, 0, 0, 0, 0, coreR);
          cg.addColorStop(0, 'rgba(255,252,235,0.96)');
          cg.addColorStop(0.32, `rgba(${gal.r0},${gal.g0},${gal.b0},0.66)`);
          cg.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = cg;
          ctx.beginPath();
          ctx.ellipse(0, 0, coreR, coreR * flatClamp, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        
        ctx.restore();
      }
    }

    function drawDiskHalf(front: boolean) {
      const sa = front ? 0 : Math.PI;
      const ea = front ? Math.PI : Math.PI * 2;
      const ratio = 0.22;
      const pb = bh.pulse;
      const rings = [
        { rm: 3.8, lw: 22, r: 255, g: 148, b: 28, a: front ? 0.14 : 0.08 },
        { rm: 3.1, lw: 15, r: 255, g: 118, b: 14, a: front ? 0.22 : 0.13 },
        { rm: 2.3, lw: 11, r: 255, g: 88, b: 5, a: front ? 0.33 : 0.20 },
        { rm: 1.7, lw: 7, r: 255, g: 188, b: 62, a: front ? 0.50 : 0.31 },
        { rm: 1.3, lw: 5, r: 255, g: 225, b: 108, a: front ? 0.66 : 0.42 },
        { rm: 1.1, lw: 3, r: 255, g: 244, b: 158, a: front ? 0.80 : 0.54 },
      ];
      for (const rg of rings) {
        const r = rg.rm * bh.r;
        const boost = 1 + pb * 0.7;
        ctx.strokeStyle = `rgba(${rg.r},${rg.g},${rg.b},${Math.min(1, rg.a * boost)})`;
        ctx.lineWidth = rg.lw * (1 + pb * 0.4);
        ctx.beginPath();
        ctx.ellipse(bh.x, bh.y, r, r * ratio, 0, sa, ea, false);
        ctx.stroke();
      }
    }

    function drawBH() {
      const pb = bh.pulse;
      const glowR = bh.lr * (1.5 + pb * 0.8);
      const og = ctx.createRadialGradient(bh.x, bh.y, bh.r, bh.x, bh.y, glowR);
      og.addColorStop(0, `rgba(255,112,10,${0.40 + pb * 0.38})`);
      og.addColorStop(0.25, `rgba(200,50,8,${0.18 + pb * 0.22})`);
      og.addColorStop(0.55, `rgba(85,10,165,${0.10 + pb * 0.15})`);
      og.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = og;
      ctx.beginPath();
      ctx.arc(bh.x, bh.y, glowR, 0, Math.PI * 2);
      ctx.fill();
      
      if (pb > 0) {
        const rimG = ctx.createRadialGradient(bh.x, bh.y, bh.r * 0.9, bh.x, bh.y, bh.r * 2.4);
        rimG.addColorStop(0, `rgba(255,200,80,${pb * 0.55})`);
        rimG.addColorStop(0.5, `rgba(255,140,30,${pb * 0.28})`);
        rimG.addColorStop(1, 'rgba(255,80,10,0)');
        ctx.fillStyle = rimG;
        ctx.beginPath();
        ctx.arc(bh.x, bh.y, bh.r * 2.4, 0, Math.PI * 2);
        ctx.fill();
      }
      
      if (bh.charging && pb > 0.01) {
        const arcEnd = -Math.PI / 2 + pb * Math.PI * 2;
        ctx.strokeStyle = 'rgba(255,180,50,0.12)';
        ctx.lineWidth = 7;
        ctx.beginPath();
        ctx.arc(bh.x, bh.y, bh.r + 11, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2, false);
        ctx.stroke();
        ctx.strokeStyle = `rgba(255,${200 + Math.floor(pb * 55)},${60 + Math.floor(pb * 100)},${0.45 + pb * 0.5})`;
        ctx.lineWidth = 3.5;
        ctx.beginPath();
        ctx.arc(bh.x, bh.y, bh.r + 11, -Math.PI / 2, arcEnd, false);
        ctx.stroke();
        ctx.fillStyle = `rgba(255,240,160,${0.6 + pb * 0.4})`;
        ctx.beginPath();
        ctx.arc(bh.x + Math.cos(arcEnd) * (bh.r + 11), bh.y + Math.sin(arcEnd) * (bh.r + 11), 2.5 + pb * 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
      
      for (const dir of [-1, 1]) {
        const jLen = bh.r * 5 + pb * bh.r * 16;
        const jy = bh.y + dir * bh.r;
        const baseA = 0.24 + pb * 0.62;
        const jg = ctx.createLinearGradient(bh.x, jy, bh.x, jy + dir * jLen);
        jg.addColorStop(0, `rgba(138,88,255,${baseA})`);
        jg.addColorStop(0.5, `rgba(90,48,228,${baseA * 0.38})`);
        jg.addColorStop(1, 'rgba(38,8,178,0)');
        ctx.fillStyle = jg;
        ctx.beginPath();
        ctx.ellipse(bh.x, jy + dir * jLen * 0.5, bh.r * (0.32 + pb * 0.28), jLen * 0.5, 0, 0, Math.PI * 2);
        ctx.fill();
        
        if (pb > 0.05) {
          const cLen = jLen * 0.75;
          const cg = ctx.createLinearGradient(bh.x, jy, bh.x, jy + dir * cLen);
          cg.addColorStop(0, `rgba(220,185,255,${pb * 0.75})`);
          cg.addColorStop(0.4, `rgba(180,130,255,${pb * 0.45})`);
          cg.addColorStop(1, 'rgba(120,60,255,0)');
          ctx.fillStyle = cg;
          ctx.beginPath();
          ctx.ellipse(bh.x, jy + dir * cLen * 0.5, bh.r * (0.10 + pb * 0.08), cLen * 0.5, 0, 0, Math.PI * 2);
          ctx.fill();
          
          const tipY = jy + dir * jLen;
          const tg = ctx.createRadialGradient(bh.x, tipY, 0, bh.x, tipY, bh.r * (0.8 + pb));
          tg.addColorStop(0, `rgba(240,220,255,${pb * 0.6})`);
          tg.addColorStop(1, 'rgba(140,80,255,0)');
          ctx.fillStyle = tg;
          ctx.beginPath();
          ctx.arc(bh.x, tipY, bh.r * (0.8 + pb), 0, Math.PI * 2);
          ctx.fill();
        }
      }
      
      drawDiskHalf(false);
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.arc(bh.x, bh.y, bh.r, 0, Math.PI * 2);
      ctx.fill();
      drawDiskHalf(true);
      
      ctx.strokeStyle = `rgba(255,175,65,${0.09 + pb * 0.18})`;
      ctx.lineWidth = 1.2 + pb * 1.5;
      ctx.beginPath();
      ctx.arc(bh.x, bh.y, bh.r * 1.48, 0, Math.PI * 2);
      ctx.stroke();
    }

    function loop() {
      frame++;
      animationFrameId = requestAnimationFrame(loop);
      
      ctx.fillStyle = '#020810';
      ctx.fillRect(0, 0, W, H);
      
      drawNebulae();
      drawGalaxies();
      
      bh.x += bh.vx;
      bh.y += bh.vy;
      const pad = bh.lr + 30;
      if (bh.x < -pad) bh.x = W + pad;
      if (bh.x > W + pad) bh.x = -pad;
      if (bh.y < -pad) bh.y = H + pad;
      if (bh.y > H + pad) bh.y = -pad;
      
      const prev = bh.pulse;
      if (bh.charging) {
        bh.pulse = Math.min(1, bh.pulse + CHARGE_RATE);
        if (prev < 0.30 && bh.pulse >= 0.30) shockwaves.push({ x: bh.x, y: bh.y, r: bh.r * 1.1, alpha: 0.42, speed: 2.5 });
        if (prev < 0.55 && bh.pulse >= 0.55) shockwaves.push({ x: bh.x, y: bh.y, r: bh.r * 1.1, alpha: 0.55, speed: 3.0 });
        if (prev < 0.80 && bh.pulse >= 0.80) shockwaves.push({ x: bh.x, y: bh.y, r: bh.r * 1.1, alpha: 0.65, speed: 3.5 });
        if (prev < 1.00 && bh.pulse >= 1.00) {
          bh.flashFrame = frame;
          for (let i = 0; i < 6; i++) shockwaves.push({ x: bh.x, y: bh.y, r: bh.r * 1.1, alpha: 0.82 - i * 0.08, speed: 2.5 + i * 0.9 });
        }
      } else {
        bh.pulse = Math.max(0, bh.pulse - DECAY_RATE);
        if (frame % 24 === 0 && bh.pulse > 0.25)
          shockwaves.push({ x: bh.x, y: bh.y, r: bh.r * 1.1, alpha: bh.pulse * 0.50, speed: 2.8 + bh.pulse * 1.5 });
      }
      
      for (let i = shockwaves.length - 1; i >= 0; i--) {
        const sw = shockwaves[i];
        sw.r += sw.speed;
        sw.alpha -= 0.013;
        if (sw.alpha <= 0) {
          shockwaves.splice(i, 1);
          continue;
        }
        ctx.strokeStyle = `rgba(200,140,255,${sw.alpha})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(sw.x, sw.y, sw.r, 0, Math.PI * 2);
        ctx.stroke();
        if (sw.r > bh.r * 2) {
          ctx.strokeStyle = `rgba(255,180,80,${sw.alpha * 0.5})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(sw.x, sw.y, sw.r * 0.65, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      
      for (const s of stars) {
        const tw = Math.sin(frame * s.ts + s.to) * 0.2 + 0.8;
        const pos = lensed(s.x, s.y);
        if (!pos) continue;
        const al = s.base * tw * pos.a;
        const col = s.sat > 0 ? `hsl(${s.hue},${s.sat}%,${s.lit}%)` : '#fff';
        ctx.globalAlpha = al;
        ctx.fillStyle = col;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, s.r * pos.s, 0, Math.PI * 2);
        ctx.fill();
        
        if (s.giant && s.r > 1.5) {
          const sl = s.r * 3.5;
          ctx.strokeStyle = col;
          ctx.lineWidth = s.r * 0.20;
          ctx.globalAlpha = al * 0.28;
          ctx.beginPath();
          ctx.moveTo(pos.x - sl, pos.y);
          ctx.lineTo(pos.x + sl, pos.y);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(pos.x, pos.y - sl);
          ctx.lineTo(pos.x, pos.y + sl);
          ctx.stroke();
          ctx.globalAlpha = al * 0.10;
          ctx.lineWidth = s.r * 0.14;
          const dl = sl * 0.55;
          ctx.beginPath();
          ctx.moveTo(pos.x - dl, pos.y - dl);
          ctx.lineTo(pos.x + dl, pos.y + dl);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(pos.x + dl, pos.y - dl);
          ctx.lineTo(pos.x - dl, pos.y + dl);
          ctx.stroke();
        }
      }
      ctx.globalAlpha = 1;
      
      drawBH();
      
      const fd = frame - bh.flashFrame;
      if (fd >= 0 && fd < 18) {
        const ft = 1 - fd / 18;
        ctx.fillStyle = `rgba(255,235,180,${ft * ft * 0.20})`;
        ctx.fillRect(0, 0, W, H);
      }
      
      shootTimer++;
      if (shootTimer > 185 + Math.random() * 255) {
        spawnShoot(null, null, false);
        if (Math.random() < 0.38) setTimeout(() => spawnShoot(null, null, false), 260 + Math.random() * 500);
        shootTimer = 0;
      }
      
      for (let i = shoots.length - 1; i >= 0; i--) {
        const s = shoots[i];
        s.trail.unshift({ x: s.x, y: s.y });
        if (s.trail.length > s.tl) s.trail.pop();
        s.x += s.vx;
        s.y += s.vy;
        s.life -= s.decay;
        if (s.life <= 0 || s.x > W + 250 || s.y > H + 250 || s.x < -250) {
          shoots.splice(i, 1);
          continue;
        }
        for (let j = 1; j < s.trail.length; j++) {
          const t = 1 - j / s.trail.length;
          ctx.strokeStyle = s.fromMouse
            ? `rgba(175,208,255,${t * t * s.life * 0.88})`
            : `rgba(255,255,255,${t * t * s.life * 0.88})`;
          ctx.lineWidth = t * 2.8;
          ctx.beginPath();
          ctx.moveTo(s.trail[j - 1].x, s.trail[j - 1].y);
          ctx.lineTo(s.trail[j].x, s.trail[j].y);
          ctx.stroke();
        }
        ctx.fillStyle = s.fromMouse ? `rgba(200,220,255,${s.life})` : `rgba(255,255,255,${s.life})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function getPos(cx: number, cy: number) {
      const r = canvas.getBoundingClientRect();
      return { x: (cx - r.left) * (W / r.width), y: (cy - r.top) * (H / r.height) };
    }

    function onDown(cx: number, cy: number) {
      if (Math.hypot(cx - bh.x, cy - bh.y) < bh.r * 2.2) bh.charging = true;
      else spawnShoot(cx, cy, true);
    }

    function onUp() {
      bh.charging = false;
    }

    const isInteractive = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof HTMLElement)) return false;
      const el = target as HTMLElement;
      if (el.tagName === 'BUTTON' || el.tagName === 'A' || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return true;
      if (el.closest('button, a, input, textarea, [role="button"], .pulse-card, [style*="cursor: pointer"], [style*="cursor:pointer"]')) return true;
      if (el.onclick || el.getAttribute('role') === 'button') return true;
      return false;
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (isInteractive(e.target)) return;
      const p = getPos(e.clientX, e.clientY);
      onDown(p.x, p.y);
    };

    const handleMouseUp = () => onUp();

    const handleMouseMove = (e: MouseEvent) => {
      if (isInteractive(e.target)) return;
      const p = getPos(e.clientX, e.clientY);
      if (!bh.charging && Math.hypot(p.x - lmx, p.y - lmy) > 45) {
        spawnShoot(p.x, p.y, false);
        lmx = p.x;
        lmy = p.y;
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (isInteractive(e.target)) return;
      const p = getPos(e.touches[0].clientX, e.touches[0].clientY);
      onDown(p.x, p.y);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isInteractive(e.target)) return;
      if (bh.charging) return;
      const p = getPos(e.touches[0].clientX, e.touches[0].clientY);
      if (Math.hypot(p.x - lmx, p.y - lmy) > 45) {
        spawnShoot(p.x, p.y, false);
        lmx = p.x;
        lmy = p.y;
      }
    };

    const handleTouchEnd = () => onUp();

    const handleResize = () => resize();

    // Use document-level listeners so canvas works even behind content
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('resize', handleResize);

    // Initialize
    resize();
    loop();

    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        id="space-canvas"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
    </>
  );
}
